import type { Device } from '@nuxtjs/device/runtime/types/index'
import { deepCopy } from '@/utils/objects'
import { SCREEN } from '@/utils/segment'
import type { OfflineAgent, OfflineAgentCommand } from '@/composables/useSamGpt/types'
import type { InvestmentProjectSummary } from '@/server/api/buyer-front/types'
import type { SearchFilters } from '@/components/SearchFilters/types'
import type { SobreplanosDataServiceMethods } from '@/services/sobreplanosDataService/types'
import type { Filter, SearchResults } from '@/composables/useOnboarding/types'
import type { TrackEventParams } from '@/services/snowplowService/types'
import type { OnboardingOfflineAgentConfig } from '@/components/OnboardingContent/utils/OnboardingOfflineAgent/types'
import SearchService from '@/composables/useOnboarding/services/SearchService/v1'
import { getBusinessHubsFromClustersValues, getBusinessHubsFromNeighborhoodsValues, getLocationTitle } from '@/utils/locations'
import { bedroomsFilter, deliveryTermFilter, neighborhoodsFilter } from '@/composables/useOnboarding/filters'
import { isTruthyFilter, extractListingIdsFromProjectsSummaries } from '@/composables/useOnboarding/utils'
import { extractValidFilters } from '@/components/SearchFilters/utils'

const opportunityTypeEventMap: Record<string, string> = {
  living: 'LIVING',
  investing: 'INVESTMENT'
}
export default class OnboardingOfflineAgent implements OfflineAgent {
  private t: any
  private device: Device
  private trackEvent: (params: TrackEventParams) => void
  private countryCode: any
  private $sobreplanosDataService: SobreplanosDataServiceMethods

  private customTitle: string | undefined

  private searchService: SearchService

  public currentFilters: SearchFilters

  constructor (config: OnboardingOfflineAgentConfig, countryCode = 'mx') {
    this.t = config.t
    this.device = config.device
    this.trackEvent = config.$trackEvent
    this.countryCode = countryCode
    this.$sobreplanosDataService = config.$sobreplanosDataService

    this.customTitle = config.customTitle
    this.currentFilters = config.initialFilters

    this.searchService = new SearchService({
      $sobreplanosDataService: this.$sobreplanosDataService,
      locationsQueryUtils: config.locationsQueryUtils,
      customTitle: this.customTitle
    })
  }

  async sendMessage (messagePayload: { messageId: string; message: string; metadata: any }) {
    const answer = await this.processMessage(messagePayload)

    return {
      command: answer
    }
  }

  requestGreeting (messagePayload: { messageId: string }) {
    return {
      command: this.getOpportunityTypeMessage({ messageId: messagePayload.messageId })
    }
  }

  async processMessage (messagePayload: { messageId: string; message: string; metadata: any }): Promise<OfflineAgentCommand> {
    this.updateCurrentFilter(messagePayload.metadata.reference, messagePayload.metadata.value)

    const currentFilter = messagePayload.metadata.reference
    const nextFilter = this.getNextFilter(currentFilter)

    if (!nextFilter) return this.getOpportunityTypeMessage({ messageId: messagePayload.messageId })

    const searchResults = await this.searchProjectsByFilters(this.currentFilters)
    const exactMatchResults = [searchResults.outstandingProject, ...searchResults.exactMatchProjects]

    if (nextFilter === 'recommendation') return this.getRecommendationMessage({ searchResults })

    const response = {
      deliveryTerm: this.getDeliveryTermMessage({ messageId: messagePayload.messageId }),
      bedroomsNumber: this.getBedroomsNumberMessage({ messageId: messagePayload.messageId })
    }[nextFilter]

    this.appendCurrentResultsSummaryMessage({ currentFilter, nextFilter, currentMessage: response, exactMatchResults })

    return response
  }

  public updateCurrentFilter (filterName: string, value: any) {
    (this.currentFilters as any)[filterName] = value
  }

  public updateCurrentFilters (filters: SearchFilters) {
    this.currentFilters = filters
  }

  sendListingsSearchEvent (listingIds: { id: string }[]) {
    const filtersData: any = {}

    if (this.currentFilters.clusters?.length) filtersData.locations = getBusinessHubsFromClustersValues(this.currentFilters.clusters, this.countryCode)

    if (this.currentFilters.neighborhoods?.length) filtersData.locations = getBusinessHubsFromNeighborhoodsValues(this.currentFilters.neighborhoods, this.countryCode)

    if (this.currentFilters.opportunityType && opportunityTypeEventMap[this.currentFilters.opportunityType]) filtersData.opportunity_type = opportunityTypeEventMap[this.currentFilters.opportunityType]

    if (this.currentFilters.deliveryTerm) filtersData.delivery_term = this.currentFilters.deliveryTerm

    if (this.currentFilters.bedroomsNumber) filtersData.bedrooms_number = this.currentFilters.bedroomsNumber

    this.trackEvent({
      name: 'listings_searched',
      version: '2-0-0',
      data: {
        listings: listingIds,
        locations: filtersData.locations || [],
        screen: SCREEN.INVESTMENT_QUIZ,
        device_platform: this.device.isMobileOrTablet ? 'WEB_MOBILE' : 'DESKTOP',
        user_action: 'USER_APPLIED_FILTERS',
        ...filtersData
      }
    })
  }

  buildFiltersTextList (filters: SearchFilters) {
    const excludedFilters = {
      clusters: [],
      neighborhoods: []
    }

    const validFilters = extractValidFilters({
      ...filters,
      ...excludedFilters
    })
    const filtersToMap = Object.keys(validFilters)

    const mappedFilters = filtersToMap.map((filterName) => {
      const value = validFilters[filterName]

      if (filterName === 'bedroomsNumber') return this.t(`offlineAgent.filtersMap.${filterName}.count`, value, { count: value })

      return this.t(`offlineAgent.filtersMap.${filterName}.${value}`)
    })

    const concatenatedFiltersWithComma = mappedFilters.slice(0, -1).join(', ')
    const lastFilter = mappedFilters.slice(-1)[0]

    return concatenatedFiltersWithComma ? `${concatenatedFiltersWithComma} ${this.t('and')} ${lastFilter}` : lastFilter
  }

  getNextFilter (currentFilter: string) {
    const hasDeliveryTerm = this.currentFilters.deliveryTerm !== null
    const hasOpportunityType = this.currentFilters.opportunityType !== null
    const isNotLiving = this.currentFilters.opportunityType !== 'living'
    const isLivingWithoutBedrooms = this.currentFilters.bedroomsNumber == null && this.currentFilters.opportunityType === 'living'
    const isLivingWithBedrooms = this.currentFilters.bedroomsNumber !== null && this.currentFilters.opportunityType === 'living'

    if (hasOpportunityType && hasDeliveryTerm && isNotLiving) {
      return 'recommendation'
    } else if (hasOpportunityType && hasDeliveryTerm && isLivingWithBedrooms) {
      return 'recommendation'
    } else if (hasDeliveryTerm && isLivingWithoutBedrooms) {
      return 'bedroomsNumber'
    }

    if (currentFilter === 'opportunityType') return 'deliveryTerm'

    if (currentFilter === 'deliveryTerm') {
      if (['', 'living'].includes(this.currentFilters.opportunityType as string)) return 'bedroomsNumber'

      return 'recommendation'
    }

    return currentFilter === 'bedroomsNumber' ? 'recommendation' : null
  }

  async searchProjectsByFilters (currentFilters: SearchFilters) {
    const filtersArray = [
      {
        key: 'delivery_term',
        type: 'frontend',
        value: currentFilters.deliveryTerm,
        filter: deliveryTermFilter
      },
      {
        key: 'bedrooms_number',
        type: 'frontend',
        value: currentFilters.bedroomsNumber,
        filter: bedroomsFilter
      },
      {
        key: 'neighborhoods',
        type: 'frontend',
        value: currentFilters.neighborhoods,
        filter: neighborhoodsFilter
      },
      {
        key: 'business_hubs',
        type: 'backend',
        value: getBusinessHubsFromNeighborhoodsValues(currentFilters.neighborhoods, this.countryCode)
      }
    ].filter(filter => isTruthyFilter(filter as Filter)) as Filter[]

    return await this.searchService.search(filtersArray)
  }

  appendCurrentResultsSummaryMessage (payload: { currentFilter: string, nextFilter: string, currentMessage: OfflineAgentCommand, exactMatchResults: InvestmentProjectSummary[] }) {
    const filtersTextList = this.buildFiltersTextList(this.currentFilters)

    const passiveRecommendationWithImages = {
      reference: payload.nextFilter,
      message: this.t('offlineAgent.passiveRecommendationsWithImages.default.message', { filters: filtersTextList }, payload.exactMatchResults.length),
      type: 'exact-match',
      results: payload.exactMatchResults.map(project => ({
        code: project.code,
        name: project.name,
        cover: project.cover,
        pictures_v2: project.pictures_v2
      })),
      filters: deepCopy(this.currentFilters) as SearchFilters
    }

    if (!payload.currentMessage.metadata) {
      payload.currentMessage.metadata = { passive_recommendations_with_images: passiveRecommendationWithImages }
      return
    }

    payload.currentMessage.metadata.passive_recommendations_with_images = passiveRecommendationWithImages
  }

  getOpportunityTypeMessage (payload: { messageId: string; }): OfflineAgentCommand {
    const locationTitle = getLocationTitle(this.customTitle ?? '', this.currentFilters, this.t, this.countryCode)

    return {
      arguments: {
        text: this.t('offlineAgent.messages.opportunityType.message', { location: locationTitle })
      },
      metadata: {
        suggested_messages: {
          reference: 'opportunityType',
          destination_agent: 'offline',
          answers_to: payload.messageId,
          skip_option: {
            label: this.t('offlineAgent.messages.skip'),
            value: ''
          },
          messages: [
            {
              label: this.t('offlineAgent.messages.opportunityType.suggestions.investing'),
              value: 'investing',
              icon: 'analytics'
            },
            {
              label: this.t('offlineAgent.messages.opportunityType.suggestions.living'),
              value: 'living',
              icon: 'house'
            },
            {
              label: this.t('offlineAgent.messages.opportunityType.suggestions.all'),
              value: '',
              icon: 'dashboard'
            }
          ]
        }
      }
    }
  }

  getDeliveryTermMessage (payload: { messageId: string }): OfflineAgentCommand {
    const { opportunityType } = this.currentFilters

    return {
      arguments: {
        text: this.t('offlineAgent.messages.deliveryTerm.message', { opportunityType })
      },
      metadata: {
        suggested_messages: {
          reference: 'deliveryTerm',
          destination_agent: 'offline',
          answers_to: payload.messageId,
          skip_option: {
            label: this.t('offlineAgent.messages.skip'),
            value: ''
          },
          messages: [
            {
              label: this.t('offlineAgent.messages.deliveryTerm.suggestions.immediate'),
              value: 'immediate',
              icon: 'document-checkmark'
            },
            {
              label: this.t('offlineAgent.messages.deliveryTerm.suggestions.3-to-6-months'),
              value: '3-to-6-months',
              icon: 'calendar'
            },
            {
              label: this.t('offlineAgent.messages.deliveryTerm.suggestions.more-than-6-months'),
              value: 'more-than-6-months',
              icon: 'calendar-assignation'
            },
            {
              label: this.t('offlineAgent.messages.opportunityType.suggestions.all'),
              value: '',
              icon: 'dashboard'
            }
          ]
        }
      }
    }
  }

  getBedroomsNumberMessage (payload: { messageId: string }): OfflineAgentCommand {
    return {
      arguments: {
        text: this.t('offlineAgent.messages.bedroomsNumber.message')
      },
      metadata: {
        suggested_messages: {
          reference: 'bedroomsNumber',
          destination_agent: 'offline',
          answers_to: payload.messageId,
          skip_option: {
            label: this.t('offlineAgent.messages.skip'),
            value: ''
          },
          messages: [
            {
              label: this.t('offlineAgent.messages.bedroomsNumber.suggestions.1'),
              value: 1,
              icon: ''
            },
            {
              label: this.t('offlineAgent.messages.bedroomsNumber.suggestions.2'),
              value: 2,
              icon: ''
            },
            {
              label: this.t('offlineAgent.messages.bedroomsNumber.suggestions.3'),
              value: 3,
              icon: ''
            },
            {
              label: this.t('offlineAgent.messages.bedroomsNumber.suggestions.not_sure'),
              value: '',
              icon: ''
            }
          ]
        }
      }
    }
  }

  getRecommendationMessage (payload: { searchResults: SearchResults }): OfflineAgentCommand {
    const { outstandingProject, exactMatchProjects, extendedSearch, extraResults } = payload.searchResults

    const exactMatchIds = extractListingIdsFromProjectsSummaries(exactMatchProjects)
    const alternatives = [...extendedSearch, ...extraResults].map(result => result.projects).flat()

    if (!outstandingProject) {
      return {
        arguments: {
          text: ''
        },
        metadata: {
          passive_recommendations_with_images: {
            reference: 'recommendation',
            type: 'extended-match',
            message: this.t('offlineAgent.passiveRecommendationsWithImages.notExact.message', exactMatchProjects.length),
            results: alternatives.map(project => ({
              code: project.code,
              name: project.name,
              cover: project.cover,
              pictures_v2: project.pictures_v2
            })),
            filters: deepCopy(this.currentFilters) as SearchFilters
          }
        }
      }
    }

    this.sendListingsSearchEvent([{ id: outstandingProject.code }, ...exactMatchIds])

    const filtersTextList = this.buildFiltersTextList(this.currentFilters)

    return {
      arguments: {
        text: this.t('offlineAgent.messages.recommendation.message', { filters: filtersTextList })
      },
      metadata: {
        outstanding_recommendation: {
          card_params: {
            name: outstandingProject.name,
            min_price: outstandingProject.price,
            currency: outstandingProject.currency,
            cover_image: outstandingProject.cover,
            path: outstandingProject.path
          },
          result: {
            code: outstandingProject.code
          }
        },
        passive_recommendations_with_images: {
          reference: 'recommendation',
          message: this.t('offlineAgent.passiveRecommendationsWithImages.additionalToOutstanding.message', exactMatchProjects.length),
          type: 'exact-match',
          results: exactMatchProjects.map(project => ({
            code: project.code,
            name: project.name,
            cover: project.cover,
            pictures_v2: project.pictures_v2
          })),
          filters: deepCopy(this.currentFilters) as SearchFilters
        }
      }
    }
  }
}
